import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { loginSuccess } from "./authSlice";
import { API_PORTAL_URL } from "@/lib/config";

export interface ILogin {
  UserName?: string;
  MobileNo?: string;
  Password: string;
  LoginType: "User" | "Employee";
}

export interface ISendOtpPayload {
  MobileNo: string;
  FirstName: string;
  LastName: string;
  EmailID: string;
  BrowserVersion?: string;
  DeviceId?: string;
  SessionId?: string;
  DeviceType?: string;
}

export interface IVerifyOtpPayload {
  MobileNo: string;
  Otp: string;
  BrowserVersion?: string;
  DeviceId?: string;
  SessionId?: string;
  DeviceType?: string;
}

// Async thunk for User/Employee Login
export const userEmployeeLogin = createAsyncThunk(
  "UserLogin/UserLogin",
  async (data: ILogin, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(`${API_PORTAL_URL}/UserLogin/UserLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const json = await response.json();

      if (!response.ok) {
        const message = json?.Details || "Something went wrong";
        return rejectWithValue(message);
      }

      const loginData = json?.Data ?? json;
      // Dispatch to authSlice to store auth state
      dispatch(loginSuccess(loginData));
      return loginData;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Something went wrong");
    }
  },
);

// Async thunk for Customer Login (Username/Password)
export const customerLogin = createAsyncThunk(
  "Customer/CustomerLogin",
  async (
    data: { UserName: string; Password: string },
    { dispatch, rejectWithValue },
  ) => {
    try {
      const response = await fetch(`${API_PORTAL_URL}/Customer/CustomerLogin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });

      const json = await response.json();

      if (!response.ok) {
        const message = json?.Details || "Something went wrong";
        return rejectWithValue(message);
      }

      const loginData = json?.Data ?? json;
      // Dispatch to authSlice to store auth state
      dispatch(loginSuccess(loginData));
      return loginData;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Something went wrong");
    }
  },
);

// Async thunk for Sending Customer OTP (India Login)
export const sendCustomerOtp = createAsyncThunk(
  "CustomerLogin/CustomerSignup",
  async (data: ISendOtpPayload, { rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_PORTAL_URL}/CustomerLogin/CustomerSignup`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      const json = await response.json();

      if (!response.ok) {
        const message =
          json?.Details ||
          json?.message ||
          "Failed to send OTP. Please try again.";
        return rejectWithValue(message);
      }

      return json?.Data ?? json;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to send OTP");
    }
  },
);

// Async thunk for Verifying Customer OTP & Logging In (India Login)
export const verifyCustomerOtp = createAsyncThunk(
  "CustomerLogin/CustomerLogin",
  async (data: IVerifyOtpPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetch(
        `${API_PORTAL_URL}/CustomerLogin/CustomerLogin`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        },
      );

      const json = await response.json();

      if (!response.ok) {
        const message =
          json?.Details || json?.message || "Invalid OTP. Please try again.";
        return rejectWithValue(message);
      }

      const loginData = json?.Data ?? json;
      // Dispatch to authSlice to store auth state
      dispatch(loginSuccess(loginData));
      return loginData;
    } catch (error: any) {
      return rejectWithValue(error?.message || "Failed to verify OTP");
    }
  },
);

interface LoginState {
  isLoading: boolean;
  isOtpSending: boolean;
  isOtpSent: boolean;
  error: string | null;
  otpSuccessMessage: string | null;
}

const initialState: LoginState = {
  isLoading: false,
  isOtpSending: false,
  isOtpSent: false,
  error: null,
  otpSuccessMessage: null,
};

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    clearLoginError: (state) => {
      state.error = null;
      state.otpSuccessMessage = null;
    },
    resetOtpState: (state) => {
      state.isOtpSending = false;
      state.isOtpSent = false;
      state.error = null;
      state.otpSuccessMessage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // User / Employee Login
      .addCase(userEmployeeLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(userEmployeeLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(userEmployeeLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Customer Login (Password)
      .addCase(customerLogin.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(customerLogin.fulfilled, (state) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(customerLogin.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Send OTP
      .addCase(sendCustomerOtp.pending, (state) => {
        state.isOtpSending = true;
        state.error = null;
      })
      .addCase(sendCustomerOtp.fulfilled, (state) => {
        state.isOtpSending = false;
        state.isOtpSent = true;
        state.error = null;
        state.otpSuccessMessage =
          "OTP sent successfully to your mobile number.";
      })
      .addCase(sendCustomerOtp.rejected, (state, action) => {
        state.isOtpSending = false;
        state.error = action.payload as string;
      })
      // Verify OTP
      .addCase(verifyCustomerOtp.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyCustomerOtp.fulfilled, (state) => {
        state.isLoading = false;
        state.isOtpSent = false;
        state.error = null;
      })
      .addCase(verifyCustomerOtp.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearLoginError, resetOtpState } = loginSlice.actions;
export default loginSlice.reducer;
